import { Keyboard, gamepadButtons } from './input.js';

export class Player extends EventTarget {
  constructor(canvas, { workerUrl = new URL('./worker.js', import.meta.url) } = {}) {
    super();
    this.canvas = canvas;
    this.context = canvas.getContext('2d', { alpha: false });
    this.worker = new Worker(workerUrl, { type: 'module' });
    this.pending = new Map();
    this.serial = 0;
    this.disposed = false;
    this.running = false;
    this.keyboard = new Keyboard('48k');
    this.listeners = new AbortController();
    this.worker.onmessage = ({ data }) => {
      if (data.event === 'frame') this.draw(data);
      else if (data.event) this.dispatchEvent(new CustomEvent(data.event, { detail: data }));
      else {
        const pending = this.pending.get(data.id);
        this.pending.delete(data.id);
        if (data.error) pending?.reject(new Error(data.error)); else pending?.resolve(data.result);
      }
    };
    this.worker.onerror = event => {
      const error = new Error(event.message || 'Emulator worker failed');
      for (const pending of this.pending.values()) pending.reject(error);
      this.pending.clear();
      this.running = false;
      this.dispatchEvent(new CustomEvent('error', { detail: { error: error.message } }));
    };
    this.bindInput();
  }
  request(type, payload = {}) {
    if (this.disposed) return Promise.reject(new Error('Player is disposed'));
    const id = ++this.serial;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, type, payload });
    });
  }
  async load(machine, config, sampleRate = 48000, assets = null) {
    this.running = false;
    this.keyboard = new Keyboard(machine);
    return this.request('init', { machine, config, sampleRate, assets });
  }
  async resume(generation = 0, clocked = false) {
    await this.request('resume', { generation, clocked });
    this.running = true;
    this.pollGamepads();
  }
  async pause() {
    this.running = false;
    this.release();
    await this.request('pause');
  }
  async reset(generation = 0) { this.release(); await this.request('reset', { generation }); }
  release() {
    this.keyboard.clear();
    this.buttons = 0;
    this.request('release').catch(() => {});
  }
  draw({ pixels, width, height, verticalScale = 1, count }) {
    const outputHeight = height * verticalScale;
    if (this.canvas.width !== width || this.canvas.height !== outputHeight) {
      this.canvas.width = width;
      this.canvas.height = outputHeight;
      this.image = new ImageData(width, outputHeight);
    }
    this.image ??= new ImageData(width, outputHeight);
    const rgba = this.image.data;
    const rowBytes = width * 4;
    for (let y = 0; y < height; y++) {
      const rowStart = y * verticalScale * rowBytes;
      for (let x = 0; x < width; x++) {
        const src = (y * width + x) * 3;
        const dst = rowStart + x * 4;
        rgba[dst] = pixels[src]; rgba[dst + 1] = pixels[src + 1]; rgba[dst + 2] = pixels[src + 2]; rgba[dst + 3] = 255;
      }
      for (let repeat = 1; repeat < verticalScale; repeat++) rgba.copyWithin(rowStart + repeat * rowBytes, rowStart, rowStart + rowBytes);
    }
    this.context.putImageData(this.image, 0, 0);
    this.frames = count;
  }
  sendInput() {
    if (!this.running) return;
    const pads = this.canvas.getRootNode().activeElement === this.canvas ? navigator.getGamepads?.() ?? [] : [];
    const state = this.keyboard.state();
    state.joystick |= gamepadButtons(pads[0]);
    state.joystick2 = gamepadButtons(pads[1]);
    this.request('input', state).catch(() => {});
  }
  pollGamepads() {
    cancelAnimationFrame(this.gamepadFrame);
    const poll = () => {
      if (!this.running || this.disposed) return;
      this.sendInput();
      this.gamepadFrame = requestAnimationFrame(poll);
    };
    poll();
  }
  bindInput() {
    const options = { signal: this.listeners.signal };
    this.canvas.tabIndex = 0;
    this.canvas.style.imageRendering = 'pixelated';
    for (const type of ['keydown', 'keyup']) this.canvas.addEventListener(type, event => {
      if (event.metaKey || !this.running) return;
      if (this.keyboard.set(event.code, event.key, type === 'keydown')) { event.preventDefault(); this.sendInput(); }
    }, options);
    this.canvas.addEventListener('blur', () => this.release(), options);
    window.addEventListener('blur', () => this.release(), options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.running) {
        this.pause().then(() => this.dispatchEvent(new Event('suspended'))).catch(() => {});
      }
    }, options);
    const mouse = event => {
      if (!this.running) return;
      event.preventDefault();
      this.canvas.focus();
      const wheel = event.type === 'wheel' ? Math.sign(-event.deltaY) : 0;
      const dx = event.type === 'mousemove' ? event.movementX : 0;
      const dy = event.type === 'mousemove' ? -event.movementY : 0;
      this.buttons = event.buttons & 7;
      this.request('mouse', { dx: Math.max(-32768, Math.min(32767, dx)), dy: Math.max(-32768, Math.min(32767, dy)), buttons: this.buttons, wheel }).catch(() => {});
    };
    for (const type of ['mousedown', 'mouseup', 'mousemove', 'wheel']) this.canvas.addEventListener(type, mouse, { ...options, passive: false });
    window.addEventListener('mouseup', event => { if (event.target !== this.canvas) this.release(); }, options);
    this.canvas.addEventListener('contextmenu', event => event.preventDefault(), options);
  }
  async dispose() {
    if (this.disposed) return;
    this.running = false;
    cancelAnimationFrame(this.gamepadFrame);
    this.listeners.abort();
    try { await this.request('dispose'); }
    finally {
      this.disposed = true;
      this.worker.terminate();
      for (const pending of this.pending.values()) pending.reject(new Error('Player disposed'));
      this.pending.clear();
    }
  }
}
